"use client";

import { usePathname } from "next/navigation";
import { useMemo, useState } from "react";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { FaqRichText } from "@/components/faq/faq-richtext";
import { resolveBg } from "@/lib/bg-color";
import { useFaqDrawer } from "@/lib/faq-context";
import { topicLabel } from "@/lib/faq-topics";
import { topicSlug, topicUrl, type FaqStory } from "@/lib/faqs";
import { regionFromPathname, regionPath } from "@/lib/region";

type FaqQuestionsBlok = SbBlokData & {
  title?: string;
  description?: string;
  topic?: string;
  max_questions?: string | number;
  questions?: string[];
  bg_color?: unknown;
};

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function FaqQuestions({ blok }: { blok: FaqQuestionsBlok }) {
  const { bg } = resolveBg(blok.bg_color, "white");
  // Look up FAQs from context (filled by the layout's fetch) so the blok ships
  // UUIDs only and bridge updates render live without `resolve_relations`.
  const { faqs: contextFaqs } = useFaqDrawer();
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const limit =
    typeof blok.max_questions === "string"
      ? parseInt(blok.max_questions, 10) || 0
      : blok.max_questions ?? 0;
  const targetTopic = blok.topic ? topicSlug(blok.topic) : "";
  const fullList = useMemo<FaqStory[]>(() => {
    if (targetTopic) {
      return contextFaqs.filter(
        (f) => topicSlug(f.content?.topic) === targetTopic,
      );
    }
    if (Array.isArray(blok.questions) && blok.questions.length > 0) {
      const byUuid = new Map(contextFaqs.map((f) => [f.uuid, f]));
      return blok.questions
        .map((uuid) => byUuid.get(uuid))
        .filter((f): f is FaqStory => f !== undefined);
    }
    return [];
  }, [targetTopic, blok.questions, contextFaqs]);
  const questions = useMemo(
    () => (limit > 0 ? fullList.slice(0, limit) : fullList),
    [fullList, limit],
  );
  const hasMore = questions.length < fullList.length;
  const linkHref =
    targetTopic && hasMore
      ? topicUrl(targetTopic, region)
      : regionPath("/faqs", region);
  const linkLabel =
    targetTopic && hasMore
      ? `View all ${topicLabel(targetTopic)} questions`
      : targetTopic
        ? "See other FAQ topics"
        : "Browse all FAQs";
  const [openUid, setOpenUid] = useState<string | null>(
    questions[0]?.uuid ?? null,
  );

  if (questions.length === 0) return null;

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-10 lg:py-[54px]">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-[21px]">
          <div className="flex flex-col gap-5 text-black lg:w-[468px] lg:shrink-0 lg:pr-[93px]">
            {blok.title && (
              <h2 className="text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08] text-purple-04">
                {blok.title}
              </h2>
            )}
            {blok.description && (
              <p className="text-body leading-[1.56] text-[#5c5c66]">
                {blok.description}
              </p>
            )}
            <a
              href={linkHref}
              className="hidden w-fit items-center gap-[7px] py-2.5 text-body font-extrabold leading-[1.054] text-purple-04 hover:underline lg:inline-flex"
            >
              {linkLabel}
              <ArrowIcon />
            </a>
          </div>

          <div className="flex flex-1 flex-col gap-6 lg:gap-0">
          <ul className="flex flex-col">
            {questions.map((faq, i) => {
              const isOpen = openUid === faq.uuid;
              const isFirst = i === 0;
              const isLast = i === questions.length - 1;
              return (
                <li
                  key={faq.uuid}
                  className={`border-t ${
                    isFirst ? "border-purple-02" : "border-purple-01"
                  } ${isLast ? "border-b border-b-purple-01" : ""}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenUid(isOpen ? null : faq.uuid)}
                    aria-expanded={isOpen}
                    className="flex w-full cursor-pointer items-start justify-between gap-6 py-5 text-left"
                  >
                    <span className="flex-1 text-body font-bold leading-[1.56] text-[#212126] lg:text-h6">
                      {faq.name}
                    </span>
                    <span
                      aria-hidden
                      className="shrink-0 text-[28px] leading-none text-purple-03"
                    >
                      {isOpen ? "–" : "+"}
                    </span>
                  </button>
                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="pb-8 pr-0 text-body leading-[1.56] text-[#5c5c66] lg:pr-12">
                        <FaqRichText node={faq.content?.answer} />
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
            <a
              href={linkHref}
              className="inline-flex w-fit items-center gap-[7px] py-2.5 text-body font-extrabold leading-[1.054] text-purple-04 hover:underline lg:hidden"
            >
              {linkLabel}
              <ArrowIcon />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
